import { useFocusEffect, useNavigation } from "@react-navigation/native";
import {
  Page,
  Stack,
  Text,
  TextField,
  Button,
} from "@towersystems/roam-common-native";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FlatList } from "react-native";
import { Spacings, View } from "react-native-ui-lib";
import PaymentMethod from "../../../../../../utilities/data/model/PaymentMethod";
import Sale from "../../../../../../utilities/data/model/Sale";
import { moneyToFormattedString } from "../../../../../../utilities/money";
import { cashRounding } from "../../../../../../utilities/round-cash";
import { usePaymentControllerProvider } from "../../../../utils/payment-controller";
import { AddPaymentOptions } from "../../../../utils/payment-controller/types";
import { PaymentScreenProps } from "../../PaymentScreen";
import { usePaymentActionsContextProvider } from "../../../PaymentRootScreen/hooks";
import { roundToDenominations } from "../../../../../../utilities/to-demonitions";
import {
  CashRounding,
  RoundingAlgorithm,
} from "../../../../../settings/components/PaymentMethodForm/types";

export interface MakeCashPaymentProps {
  paymentMethod: PaymentMethod;
  sale: Sale;
  disableUnderTender?: boolean;
  minTenderAmount?: number;
}

export const MakeCashPayment = ({
  paymentMethod,
  sale,
  minTenderAmount: _minTenderAmount,
}: MakeCashPaymentProps) => {
  const maybeParentScreenContext = usePaymentActionsContextProvider();

  const [calculating, setCalculating] = useState(false);

  const [paymentAmount, setPaymentAmount] = useState<string | undefined>(
    undefined
  );
  const [error, setError] = useState<string | undefined>(undefined);
  const navigation = useNavigation<PaymentScreenProps["navigation"]>();

  const { tenderedAmountInput, validTenderedAmount, setTenderedAmountInput } =
    usePaymentControllerProvider();

  const roundUsingPaymentMethodHandler = useCallback(
    (amount: string | number) => {
      const rounding = paymentMethod.handler?.args?.find(
        (arg) => arg.name === "cashRounding"
      )?.value;

      const algorithm = paymentMethod.handler?.args?.find(
        (arg) => arg.name === "roundingAlgorithm"
      )?.value;

      console.log("roundUsingPaymentMethodHandler", rounding, algorithm);

      return cashRounding(
        amount,
        rounding as CashRounding | undefined,
        algorithm as RoundingAlgorithm | undefined
      );
    },
    [paymentMethod]
  );

  const normalizedMinTenderAmount = useMemo(() => {
    if (_minTenderAmount) {
      return roundUsingPaymentMethodHandler(_minTenderAmount);
    }
    return undefined;
  }, [_minTenderAmount]);

  useFocusEffect(
    React.useCallback(() => {
      setTenderedAmountInput("");
      setPaymentAmount(
        roundUsingPaymentMethodHandler(tenderedAmountInput).toString()
      );
    }, [])
  );

  const normalizedValidTenderedAmount = useMemo(() => {
    if (!validTenderedAmount) {
      return false;
    }

    if (normalizedMinTenderAmount) {
      return Number(tenderedAmountInput) >= normalizedMinTenderAmount;
    }
    return true;
  }, [validTenderedAmount, tenderedAmountInput, normalizedMinTenderAmount]);

  const handleOnAddPayment = useCallback(
    async (options?: AddPaymentOptions) => {
      try {
        setCalculating(true);
        if (!paymentAmount) {
          throw new Error("Invalid payment amount");
        }
        const tenderedAmount =
          options?.tenderedAmount || options?.tenderedAmount === 0
            ? options.tenderedAmount
            : parseFloat(tenderedAmountInput);

        await maybeParentScreenContext?.onAddPayment?.({
          ...options,
          paymentAmount: paymentAmount,
          paymentMethod: paymentMethod,
          tenderedAmount: tenderedAmount.toString(),
        });
        return;
      } catch (error: any) {
        setError(error.message);
        setCalculating(false);
      }

      // must use async because of the watermelondb observables will be stale until
      // the next render
    },
    [
      paymentMethod,
      tenderedAmountInput,
      validTenderedAmount,
      paymentAmount,
      navigation,
      sale,
    ]
  );

  useEffect(() => {
    navigation.setOptions({
      title: `Pay ${moneyToFormattedString(paymentAmount)}`,
      headerRight: () => (
        <Button
          label="Collect Cash"
          isAction
          disabled={!normalizedValidTenderedAmount}
          onPress={() => handleOnAddPayment()}
          size="compact"
        ></Button>
      ),
    });
  }, [normalizedValidTenderedAmount, paymentAmount, handleOnAddPayment]);

  const demonitionOptions = useMemo(() => {
    return roundToDenominations(Number(normalizedMinTenderAmount));
  }, [normalizedMinTenderAmount]);

  const demonitionMarkup = useMemo(() => {
    return (
      <FlatList
        style={{}}
        horizontal
        scrollEnabled={true}
        contentContainerStyle={{ paddingBottom: Spacings.s4 }}
        ItemSeparatorComponent={() => (
          <View
            style={{
              height: "100%",
              width: Spacings.s4,
            }}
          ></View>
        )}
        keyExtractor={(item) => item}
        data={demonitionOptions}
        renderItem={({ item }) => {
          return (
            <Button
              onPress={async () => {
                setTenderedAmountInput(item);
                await handleOnAddPayment({ tenderedAmount: Number(item) });
              }}
              isAction
              label={moneyToFormattedString(item)}
              style={{ width: 230, height: 120 }}
              disabled={calculating}
            ></Button>
          );
        }}
      ></FlatList>
    );
  }, [
    demonitionOptions,
    setTenderedAmountInput,
    handleOnAddPayment,
    calculating,
  ]);

  return (
    <Page
      titleContainerStyle={{ alignItems: "center" }}
      title={paymentMethod.name}
    >
      <Stack vertical>
        <Text variant="heading4" fontWeight="bold" color="subdued">
          Select tendered amount
        </Text>

        <View style={{ width: "100%" }}>{demonitionMarkup}</View>
        <View>
          <View style={{ alignItems: "center" }} marginT-20>
            <View flex row style={{ width: "100%" }}>
              <View flex>
                <TextField
                  readonly={normalizedMinTenderAmount === 0}
                  placeholder="Other amount"
                  onChangeText={setTenderedAmountInput}
                  value={tenderedAmountInput}
                  onBlur={() => {
                    if (normalizedValidTenderedAmount) {
                      setTenderedAmountInput(
                        roundUsingPaymentMethodHandler(
                          tenderedAmountInput
                        ).toString()
                      );
                    }
                  }}
                  formatterType={
                    normalizedValidTenderedAmount ? "money" : undefined
                  }
                  keyboardType="numeric"
                  validationMessage={
                    tenderedAmountInput &&
                    (normalizedMinTenderAmount && !normalizedValidTenderedAmount
                      ? `Minimum tender amount is ${moneyToFormattedString(
                          normalizedMinTenderAmount
                        )}`
                      : !validTenderedAmount
                      ? "Invalid amount"
                      : undefined)
                  }
                />
              </View>
            </View>

            {error ? <Text>{error}</Text> : null}
          </View>
        </View>
      </Stack>
    </Page>
  );
};
